import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import TeamDisplay from "@/components/TeamDisplay.jsx";

export const StepWelcome = ({onStart, contestData, quizPoints = null}) => {
    const {t} = useTranslation();
    const coverUrl = contestData?.coverUrl;

    const isRugby = contestData?.type === 'RUGBY';

    const rewardLabel = (contestData?.participantUi?.rewardLabel || t('welcome.default_reward')).toUpperCase();
    const halfTimeRewardLabel = contestData?.participantUi?.halfTimeRewardLabel?.toUpperCase() || null;
    const barNameRaw = contestData?.place?.name;
    const barName = barNameRaw?.toUpperCase();

    const homeTeam = contestData?.fixture?.homeTeam;
    const awayTeam = contestData?.fixture?.awayTeam;
    const hasMatchInfo = homeTeam && awayTeam;

    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setIsReady(true), 400);
        return () => clearTimeout(timer);
    }, []);

    const logoProps = {
        w: "w-12 min-[380px]:w-20",
        h: "h-12 min-[380px]:h-20",
        flagW: "w-14 min-[380px]:w-20",
        flagH: "h-10 min-[380px]:h-14",
        textSize: "text-sm min-[380px]:text-md",
    };

    const sportKey = isRugby ? 'rugby' : 'football';

    return (
        <div className="fixed inset-0 z-50 bg-brand-dark font-sans overflow-hidden select-none">
            <style>{`
                :root { --ease-out: cubic-bezier(0.16, 1, 0.3, 1); }
                .anim-bg { transform: scale(1.15); transition: transform 10s ease-out; }
                .anim-bg-active { transform: scale(1); }
                .slide-down { animation: slideDown 0.8s var(--ease-out) forwards; opacity: 0; transform: translateY(-20px); }
                @keyframes slideDown { to { opacity: 1; transform: translateY(0); } }
                .slide-up { animation: slideUp 0.8s var(--ease-out) forwards; opacity: 0; transform: translateY(30px); }
                @keyframes slideUp { to { opacity: 1; transform: translateY(0); } }
                .zoom-in { animation: zoomIn 0.6s var(--ease-out) forwards; opacity: 0; transform: scale(0.8); }
                @keyframes zoomIn { to { opacity: 1; transform: scale(1); } }
                .delay-1 { animation-delay: 0.1s; }
                .delay-2 { animation-delay: 0.2s; }
                .delay-3 { animation-delay: 0.3s; }
                .delay-4 { animation-delay: 0.4s; }
            `}</style>

            {/* --- 1. IMAGE DE FOND (fixe, hors du flux qui défile) --- */}
            <div
                className={`absolute inset-0 z-0 bg-brand-dark transition-opacity duration-1000 ${isReady ? 'opacity-100' : 'opacity-0'}`}>
                {coverUrl && (
                    <>
                        <img
                            src={coverUrl}
                            alt="Cover"
                            className={`w-full h-full object-cover object-top filter brightness-[0.4] grayscale-20 ${isReady ? 'anim-bg-active' : 'anim-bg'}`}
                        />
                        <div
                            className="absolute inset-0 bg-linear-to-t from-brand-dark via-brand-dark/80 to-transparent"></div>
                    </>
                )}
            </div>

            {/* --- 2. CONTENU : colonne flex qui défile.
                Remplace les deux moitiés absolues (top-50% / bottom-50%) qui se chevauchaient
                sur petit écran (iPhone SE) : les drapeaux se dessinaient par-dessus le titre.
                Ici l'en-tête est en haut, le bloc drapeaux VS drapeaux est centré (flex-1) sur
                grand écran, et sur petit écran le contenu s'enchaîne / défile sans chevauchement. --- */}
            <div className="relative z-10 h-full flex flex-col overflow-y-auto">

                {/* En-tête : GOJO + nom du bar */}
                {isReady && (
                    <div className="shrink-0 pt-10 flex flex-col items-center gap-3 pointer-events-none">
                        <div className="slide-down">
                            <h1 className="text-4xl font-black text-white tracking-tight leading-none drop-shadow-md opacity-80">
                                GOJO<span className="text-yellow-400">.</span>
                            </h1>
                        </div>
                        {barName && (
                            <div
                                className="slide-down delay-1 flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 backdrop-blur-sm border border-white/10 shadow-sm">
                                <svg className="w-3 h-3 text-action-green" fill="currentColor" viewBox="0 0 24 24">
                                    <path
                                        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                                </svg>
                                <span
                                    className="text-[10px] font-bold text-gray-300 tracking-widest uppercase truncate max-w-50">
                                    {barName}
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {/* Le match : drapeau VS drapeau. flex-1 => centré verticalement quand il y a de
                    la place ; ne chevauche jamais le titre car il vit dans le même flux. */}
                <div className="flex-1 flex items-center justify-center w-full px-4 py-6 pointer-events-none">
                    {hasMatchInfo && isReady && (
                        <div className="zoom-in delay-2 flex items-center justify-center gap-6 w-full">
                            <TeamDisplay
                                {...logoProps}
                                team={homeTeam}
                                textColor="text-white drop-shadow-md"
                            />
                            <div className="flex flex-col items-center pb-6">
                                <span
                                    className="text-yellow-400 text-3xl font-black italic tracking-tighter drop-shadow-[0_0_10px_rgba(250,204,21,0.5)]">
                                    VS
                                </span>
                            </div>
                            <TeamDisplay
                                {...logoProps}
                                team={awayTeam}
                                textColor="text-white drop-shadow-md"
                            />
                        </div>
                    )}
                </div>

                {/* --- 3. CONTENU BAS (INFOS) --- */}
                <div className="shrink-0 p-6 pb-10 flex flex-col justify-end">
                    {isReady && (
                        <>
                            <h2 className="text-3xl min-[380px]:text-4xl font-black text-white uppercase leading-[0.9] tracking-tighter mb-4 sm:mb-6 slide-up delay-2 whitespace-pre-line">
                                {t('welcome.title')}
                            </h2>

                            {quizPoints != null && (
                                <div className="slide-up delay-2 mb-5 self-start inline-flex items-center gap-2 bg-action-green/15 border border-action-green/25 rounded-full pl-2.5 pr-3.5 py-1.5">
                                    <span className="text-sm leading-none">⚡️</span>
                                    <span className="text-xs font-bold text-white/90 leading-none">
                                        <span className="text-action-green font-black">+{quizPoints.toLocaleString()} pts</span>{' '}
                                        {t('welcome.quiz_bonus_short', 'quiz en poche')}
                                    </span>
                                </div>
                            )}

                            <div className="slide-up delay-3 mb-8">
                                {halfTimeRewardLabel ? (
                                    <>
                                        {/* Deux moments pour gagner : mi-temps puis coup de sifflet final */}
                                        <div className="flex items-center gap-2 mb-4">
                                            <span className="text-lg">🎁</span>
                                            <p className="text-yellow-400 text-[10px] font-bold uppercase tracking-widest">
                                                {t('welcome.two_rewards_subtitle', '2 lots à gagner ce soir')}
                                            </p>
                                        </div>

                                        <div className="flex flex-col">
                                            {/* Palier 1 : Mi-temps */}
                                            <div className="flex gap-3">
                                                <div className="flex flex-col items-center self-stretch">
                                                    <div
                                                        className="w-8 h-8 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-sm shrink-0">
                                                        ⏱️
                                                    </div>
                                                    <div
                                                        className="w-px flex-1 my-1 bg-linear-to-b from-white/25 to-yellow-400/40"/>
                                                </div>
                                                <div className="pb-5">
                                                    <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mb-1">
                                                        {t('welcome.moment_half_time', 'Mi-temps')}
                                                    </p>
                                                    <div
                                                        className="text-xl min-[380px]:text-2xl font-black text-white/90 leading-none tracking-tight drop-shadow">
                                                        {halfTimeRewardLabel}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Palier 2 : Fin du match (lot principal) */}
                                            <div className="flex gap-3">
                                                <div className="flex flex-col items-center">
                                                    <div
                                                        className="w-8 h-8 rounded-full bg-yellow-400/15 border border-yellow-400/30 flex items-center justify-center text-sm shrink-0">
                                                        🏆
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mb-1">
                                                        {t('welcome.moment_full_time', 'Coup de sifflet final')}
                                                    </p>
                                                    <div
                                                        className="text-2xl min-[380px]:text-3xl font-black text-white leading-none tracking-tight drop-shadow-lg">
                                                        {rewardLabel}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-lg">🎁</span>
                                            <p className="text-yellow-400 text-[10px] font-bold uppercase tracking-widest">
                                                {t('welcome.reward_subtitle')}
                                            </p>
                                        </div>
                                        <div
                                            className="text-2xl min-[380px]:text-3xl font-black text-white leading-none tracking-tight drop-shadow-lg">
                                            {rewardLabel}
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="slide-up delay-3 mb-8 pl-4 border-l border-white/10 flex flex-col gap-4">
                                <div>
                                    <p className="text-[9px] text-gray-500 font-bold uppercase tracking-widest mb-0.5">
                                        {t(`welcome.${sportKey}.objective_title`)}
                                    </p>
                                    <p className="text-sm font-bold text-gray-200">
                                        {t(`welcome.${sportKey}.objective_text`)}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[9px] text-gray-500 font-bold uppercase tracking-widest mb-0.5">
                                        {t(`welcome.${sportKey}.tie_title`)}
                                    </p>
                                    <p className="text-sm font-bold text-gray-200">
                                        {t(`welcome.${sportKey}.tie_text`)}
                                    </p>
                                </div>
                            </div>

                            <button
                                onClick={onStart}
                                className="w-full py-4 bg-action-green text-white font-black text-md min-[380px]:text-lg rounded-xl shadow-[0_10px_30px_-10px_rgba(16,185,129,0.5)] slide-up delay-4 hover:scale-[1.02] active:scale-98 transition-all uppercase tracking-wide"
                            >
                                {t('welcome.cta')}
                            </button>

                            <p className="text-center text-[9px] text-white/20 mt-4 slide-up delay-4">
                                {t('welcome.free_label', {barName: barNameRaw || t('welcome.house')})}
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
